import React from 'react';

import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

import ScriptsEditor, { ILoadedScripts, IOtherFiles } from '../admin/ScriptsEditor';
import PrestaGlobals from '../common/PrestaGlobals';
import { AiDesignerContext, IScriptPair } from './AiDesignerContext';

/**
 * "Szkriptek" tab of the AI designer page: the ucon + StyleScript pair the Designer shows (the
 * product's, the AI designer's last verify, or an earlier edit or file load here), editable as JSON
 * and loadable from files (ScriptsEditor).
 *
 * Applying either editor loads the ucon and then the StyleScript into the Designer, so the 3D
 * rebuilds from the pair. An edit is kept only when the AI designer tab saves it as a revision of the
 * room; the next message to the AI designer takes it along. A room scan (.usdz) dropped or picked here goes
 * to the AI designer service, as "Szoba szkennelésből" does. The admin drawer's product tools
 * (saving the script, new product) are not offered here.
 */

const SOURCE_TEXT: Record<IScriptPair['source'], string> = {
    product: 'a betöltött termék saját szkriptjei',
    ai: 'az AI tervező utoljára ellenőrzött terve',
    manual: 'itt kézzel módosítva vagy fájlból betöltve',
    scan: 'egy szobaszkennelésből (usdz) készült szoba',
    revision: 'a szoba egy mentett változata',
};

const parse = (text: string | null | undefined): unknown => {
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

const DIRTY_NOTE = 'Nem betöltött változás — az „Alkalmaz” küldi a Designernek.';

const ScriptsTab: React.FC = () => {
    const ai = React.useContext(AiDesignerContext);
    const current = ai.current;
    const [status, setStatus] = React.useState<string | null>(null);

    // Keyed on the text: applying one editor must not reload the other and drop its edits.
    const ucon = React.useMemo(() => parse(current?.ucon), [current?.ucon]);
    const style = React.useMemo(() => parse(current?.style), [current?.style]);

    const [uconName, styleName] = ai.design
        ? [ai.design.ucon.path, ai.design.style.path]
        : [`termek-${PrestaGlobals.productId}.ucon.json`, `termek-${PrestaGlobals.productId}.style.json`];

    const apply = (nextUcon: string, nextStyle: string | null, done = 'Betöltve a Designerbe.') => {
        if (!ai.applyScripts(nextUcon, nextStyle)) {
            setStatus('A Unity designer még nem töltődött be.');
            return;
        }
        setStatus(done);
    };

    const handleUcon = (parsed: any) => apply(JSON.stringify(parsed, null, 2), current?.style ?? null);

    const handleStyle = (parsed: any) => {
        if (!Array.isArray(parsed)) {
            setStatus('A stílus szkriptnek JSON tömbnek kell lennie ([ ... ]).');
            return;
        }
        if (!current) {
            setStatus('Még nincs UCon szkript a Designerben.');
            return;
        }
        apply(current.ucon, JSON.stringify(parsed, null, 1));
    };

    // A file brings one script or both; the other stays what the Designer shows.
    const handleLoad = ({ ucon: loadedUcon, style: loadedStyle }: ILoadedScripts) => {
        const nextUcon = loadedUcon !== undefined ? JSON.stringify(loadedUcon, null, 2) : current?.ucon;
        if (!nextUcon) {
            setStatus('Még nincs UCon szkript a Designerben: töltsön be egy UCon szkriptet is.');
            return;
        }
        const nextStyle = loadedStyle !== undefined ? JSON.stringify(loadedStyle, null, 1) : current?.style ?? null;
        const what = [loadedUcon !== undefined && 'UCon', loadedStyle !== undefined && 'stílus'].filter(Boolean).join(' + ');
        apply(nextUcon, nextStyle, `Betöltve a Designerbe (${what} fájlból).`);
    };

    const handleRestoreProduct = () => {
        setStatus(ai.restoreProduct() ? 'A termék saját szkriptjei betöltve a Designerbe.' : 'A Unity designer még nem töltődött be.');
    };

    const scans: IOtherFiles = {
        accept: '.usdz,model/vnd.usdz+zip',
        test: (file) => /\.usdz$/i.test(file.name) || file.type === 'model/vnd.usdz+zip',
        label: 'szobaszkennelés (.usdz)',
        onFiles: async (files) => {
            const file = files[files.length - 1];
            setStatus(`Szoba készül a szkennelésből (${file.name})…`);
            const answer = await ai.loadScan(file);
            setStatus(answer.ok
                ? `A szoba betöltve a Designerbe (${file.name}). ${answer.report.join(' ')}`
                : answer.error);
        },
    };

    const pairName = ai.design
        ? ai.design.ucon.path.replace(/(\.ucon)?\.json$/, '') + '.scripts.json'
        : `termek-${PrestaGlobals.productId}.scripts.json`;

    if (!current) {
        return (
            <Typography variant="caption" color="text.secondary">
                A termék szkriptjei még töltődnek.
            </Typography>
        );
    }

    return (
        <ScriptsEditor
            status={status}
            onLoad={handleLoad}
            pairFileName={pairName}
            onRestoreProduct={handleRestoreProduct}
            otherFiles={scans}
            intro={(
                <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        A Designerben most: <b>{SOURCE_TEXT[current.source]}</b>. Az „Alkalmaz” a UCon, majd a stílus
                        szkriptet tölti be a Designerbe. Nem kerül mentésre, és az AI tervező csak akkor látja, ha az
                        új tervezés megkapja a Designer szkriptjeit.
                    </Typography>
                    {ai.busy && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            Az AI tervező éppen dolgozik: a következő ellenőrzése felülírja, amit itt betölt.
                        </Alert>
                    )}
                </>
            )}
            ucon={{
                value: ucon,
                applyLabel: 'Alkalmaz',
                dirtyNote: DIRTY_NOTE,
                onApply: handleUcon,
                fileName: uconName,
            }}
            style={{
                value: style,
                applyLabel: 'Alkalmaz',
                dirtyNote: DIRTY_NOTE,
                onApply: handleStyle,
                fileName: styleName,
                header: current.style === null && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Nincs stílus szkript a Designerben.
                    </Typography>
                ),
            }}
        />
    );
};

export default ScriptsTab;
